import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AuthService } from '../shared/services/auth.service'

// Disk-usage indicator. Everything it shows comes from a figure the background
// collector produced earlier; refreshing re-reads that cached number and never
// triggers a measurement, which is why it can poll at all.
@Component({
  selector: 'app-storage-badge',
  templateUrl: './storage-badge.component.html',
  styleUrls: ['./storage-badge.component.scss']
})
export class StorageBadgeComponent implements OnInit, OnDestroy {
  @Input() refreshMs: number = 60000

  buying: boolean = false
  buyError: string = ''
  private timer: any = null

  constructor(public authService: AuthService){}

  ngOnInit(){
    this.reload()
    this.timer = setInterval(()=>this.reload(), this.refreshMs)
  }

  ngOnDestroy(){
    if(this.timer){ clearInterval(this.timer); this.timer = null }
  }

  reload(){
    this.authService.loadStorageStatus().subscribe(()=>{}, ()=>{})
  }

  get s(): any { return this.authService.storageStatus }

  // Nothing is shown until the collector has actually measured this account: an
  // indicator that invents a zero is worse than one that waits.
  get show(): boolean {
    return !!this.s && this.s.usedGb !== null && this.s.usedGb !== undefined
  }

  get over(): boolean { return !!this.s && this.s.state === 'over' }
  get warn(): boolean { return !!this.s && this.s.state === 'warn' }

  get barPercent(): number {
    if(!this.s || this.s.percent === null || this.s.percent === undefined) return 0
    return Math.max(0, Math.min(100, this.s.percent))
  }

  // Numbers and units are kept apart on purpose: the unit and the currency are
  // words, and words belong in the template, which is the per-language file.
  // Baking "GB" into the class is how a Ukrainian cabinet ends up reading
  // "180.0 GB з 150 GB".
  get usedInTb(): boolean { return (Number(this.s && this.s.usedGb) || 0) >= 1024 }

  get usedValue(): string {
    const g = Number(this.s && this.s.usedGb) || 0
    return this.usedInTb ? (g / 1024).toFixed(2) : g.toFixed(1)
  }

  get hasQuota(): boolean {
    return !!this.s && !this.s.unlimited && !!this.s.quotaGb
  }

  get quotaInTb(): boolean { return this.hasQuota && Number(this.s.quotaGb) >= 1024 }

  get quotaValue(): string {
    if(!this.hasQuota) return ''
    const g = Number(this.s.quotaGb)
    return this.quotaInTb ? (g / 1024).toFixed(g % 1024 === 0 ? 0 : 2) : String(g)
  }

  get priceAmount(): number {
    const p = this.authService.storageAddonPrice
    return (p && p.available && p.amount) ? p.amount : 0
  }

  buy(){
    if(this.buying) return
    this.buying = true
    this.buyError = ''
    this.authService.buyStorageTb().subscribe(
      ()=>{ this.buying = false; this.reload() },
      (err)=>{
        this.buying = false
        const reason = err && err.error && err.error.reason
        this.buyError = reason === 'insufficient_funds' ? 'not_enough' : 'failed'
      }
    )
  }
}
